'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface RegisterFormData {
  fullName: string;
  email: string;
  phone: string;
  course: string;
  guardianName: string;
  guardianPhone: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

const COURSES = [
  { id: 'course-om', value: 'OM', label: 'OM — Office Management' },
  { id: 'course-difa', value: 'DIFA', label: 'DIFA — Diploma in Financial Accounting' },
  { id: 'course-adca', value: 'ADCA', label: 'ADCA — Advanced Diploma in Computer Applications' },
  { id: 'course-dttd', value: 'DTTD', label: 'DTTD — Diploma in Desktop Publishing' },
  { id: 'course-dtp', value: 'DTP', label: 'DTP — Desktop Publishing' },
  { id: 'course-doe', value: 'DOE', label: 'DOE — Diploma in Office Efficiency' },
  { id: 'course-tally', value: 'TALLY', label: 'Tally Prime — Accounting Software' },
  { id: 'course-web', value: 'WEBDEV', label: 'Web Development Basics' },
];

export default function RegisterForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.fullName,
        role: 'student',
        phone: data.phone,
        course: data.course,
        guardianName: data.guardianName,
        guardianPhone: data.guardianPhone,
      });
      setIsLoading(false);
      setSubmitted(true);
      toast.success('Registration submitted! You can now sign in.');
    } catch (error: any) {
      setIsLoading(false);
      if (error?.message?.includes('already registered')) {
        toast.error('This email is already registered. Please sign in.');
      } else {
        toast.error(error?.message || 'Registration failed. Please try again.');
      }
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(234,179,8,0.15)', border: '2px solid rgba(234,179,8,0.4)' }}>
          <span className="text-3xl">⏳</span>
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">Registration Submitted!</h3>
        <div className="rounded-xl p-3 mb-3 text-left" style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)' }}>
          <p className="text-sm font-semibold text-yellow-700 mb-1">⏳ Status: Pending Approval</p>
          <p className="text-xs text-yellow-700">
            Your account is under review by the admin. You will receive a WhatsApp message once your enrollment is approved.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          Switch to the Login tab to sign in once approved.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
          <input
            type="text"
            {...register('fullName', { required: 'Full name is required' })}
            placeholder="Rahul Sharma"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.fullName && <p className="mt-1 text-xs text-danger">{errors.fullName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Email</label>
          <input
            type="email"
            {...register('email', { required: 'Email required' })}
            placeholder="your@email.com"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Phone (WhatsApp)</label>
          <input
            type="tel"
            {...register('phone', { required: 'Phone required', pattern: { value: /^[6-9]\d{9}$/, message: 'Invalid Indian mobile number' } })}
            placeholder="9876543210"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone.message}</p>}
        </div>

        <div className="col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1">Course</label>
          <select
            {...register('course', { required: 'Select a course' })}
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          >
            <option value="">Select your course...</option>
            {COURSES.map((c) => (
              <option key={c.id} value={c.value}>{c.label}</option>
            ))}
          </select>
          {errors.course && <p className="mt-1 text-xs text-danger">{errors.course.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Guardian Name</label>
          <input
            type="text"
            {...register('guardianName', { required: 'Required' })}
            placeholder="Parent / Guardian"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.guardianName && <p className="mt-1 text-xs text-danger">{errors.guardianName.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Guardian Phone</label>
          <input
            type="tel"
            {...register('guardianPhone', { required: 'Required' })}
            placeholder="9876543210"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.guardianPhone && <p className="mt-1 text-xs text-danger">{errors.guardianPhone.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })}
              placeholder="Min 8 characters"
              className="w-full px-3 py-2.5 pr-9 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
              style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Confirm Password</label>
          <input
            type="password"
            {...register('confirmPassword', {
              required: 'Required',
              validate: (v) => v === password || 'Passwords do not match',
            })}
            placeholder="Repeat password"
            className="w-full px-3 py-2.5 rounded-xl text-sm text-foreground placeholder-muted-foreground outline-none focus:ring-2 focus:ring-primary transition-all"
            style={{ background: 'var(--input)', border: '1px solid var(--border)' }}
          />
          {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          {...register('termsAccepted', { required: 'You must accept the terms' })}
          className="mt-0.5 w-4 h-4 rounded accent-primary"
        />
        <span className="text-xs text-muted-foreground">
          I agree to the{' '}
          <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
          {' '}and{' '}
          <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span>
        </span>
      </label>
      {errors.termsAccepted && <p className="text-xs text-danger">{errors.termsAccepted.message}</p>}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 rounded-xl font-semibold text-sm btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
        style={{ minHeight: '44px' }}
      >
        {isLoading ? <Loader2 size={18} className="animate-spin" /> : <><UserPlus size={16} /> Create Account</>}
      </button>
    </form>
  );
}